/**
 * AdminServers — Unified view of all servers (Cloud Machines + Runners + Agent Info)
 * Clean design — WhiteLabelClaw visual identity
 */
import { useState, useEffect } from 'preact/hooks';
import { T } from '../styles/tokens';
import { api } from '../api/client';
import { isLoggedIn } from '../stores/auth.store';
import { LoginPage } from './Login';
import { SkeletonBar, Button, Input, Select } from '../components/ui';

interface ServerRow {
  id: string;
  hostname: string | null;
  display_name: string | null;
  status: string | null;
  ipv4_address: string | null;
  provider: string | null;
  openclaw_installed: number | null;
  agent_name: string | null;
  created_at: string | null;
  updated_at: string | null;
  source: string;
  runner_id: string | null;
  runner_name: string | null;
  os: string | null;
  arch: string | null;
  mode: string | null;
  runner_version: string | null;
  runner_status: string | null;
  has_openclaw: number | null;
  has_docker: number | null;
  docker_version: string | null;
  node_version: string | null;
  runner_agent_model: string | null;
  last_seen_at: string | null;
  openclaw_path: string | null;
  agents_json: string | null;
  mai_agent_name: string | null;
  mai_model: string | null;
  openclaw_version: string | null;
  tools: string | null;
  skills: string | null;
  channels: string | null;
  integrations: string | null;
  env_keys: string | null;
  last_auto_sync: string | null;
  synced_at: string | null;
  env_path: string | null;
  config_path: string | null;
  config_json: string | null;
  agents_md: string | null;
  heartbeat_md: string | null;
  identity_md: string | null;
  bootstrap_md: string | null;
  customer_email: string | null;
  last_snapshot_at: string | null;
  backup_enabled: number | null;
  backup_schedule: string | null;
  backup_config_last: string | null;
  last_backup_at: string | null;
  last_backup_status: string | null;
}

interface BrainSnapshot {
  soul_md: string | null;
  user_md: string | null;
  memory_md: string | null;
  tools_md: string | null;
  snapshot_at: string | null;
}

function timeAgo(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  if (diff < 0) return 'agora';
  if (diff < 60_000) return `${Math.round(diff / 1000)}s`;
  if (diff < 3600_000) return `${Math.round(diff / 60000)}min`;
  if (diff < 86400_000) return `${Math.round(diff / 3600000)}h`;
  return `${Math.floor(diff / 86400000)}d`;
}

function formatDate(dateStr: string | null): string {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  return d.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// SVG icons for OS
function OsIcon({ os }: { os: string | null }) {
  const o = (os || '').toLowerCase();
  if (o.includes('linux')) return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E5E7EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 2C9.5 2 8 4.5 8 7v4c0 1-1 2-2.5 3S3 16 3 18c0 1.5 2 3 5 3h8c3 0 5-1.5 5-3 0-2-1-2-2.5-4S16 12 16 11V7c0-2.5-1.5-5-4-5z"/>
      <circle cx="10" cy="8" r="1" fill="#E5E7EB"/>
      <circle cx="14" cy="8" r="1" fill="#E5E7EB"/>
    </svg>
  );
  if (o.includes('macos') || o.includes('darwin')) return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E5E7EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83"/>
      <path d="M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
    </svg>
  );
  if (o.includes('windows')) return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#E5E7EB" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 12h9V3L3 4.5V12zM12 3h9v9h-9V3zM3 12v7.5L12 21v-9H3zM12 12v9l9 1.5V12h-9z"/>
    </svg>
  );
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#6B7A90" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/>
    </svg>
  );
}

function StatusDot({ status }: { status: string | null }) {
  const s = (status || '').toLowerCase();
  const isOnline = s === 'online' || s === 'running' || s === 'active';
  const isOffline = s === 'offline';
  const color = isOnline ? T.success : isOffline ? T.danger : T.warning;
  const label = isOnline ? 'Online' : isOffline ? 'Offline' : s || 'N/A';
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, fontWeight: 600, color }}>
      <span style={{ width: 7, height: 7, borderRadius: '50%', background: color, display: 'inline-block' }} />
      {label}
    </span>
  );
}

function Chip({ text, variant = 'default' }: { text: string; variant?: 'default' | 'success' | 'error' | 'info' | 'purple' | 'orange' }) {
  const colors: Record<string, { bg: string; fg: string; border: string }> = {
    default: { bg: T.border, fg: T.textSecondary, border: T.bgSecondary },
    success: { bg: '#064E3B20', fg: '#6EE7B7', border: '#06523540' },
    error: { bg: '#7F1D1D20', fg: T.danger, border: '#7F1D1D40' },
    info: { bg: '#1E3A5F20', fg: '#93C5FD', border: '#1E3A5F40' },
    purple: { bg: '#4C1D9520', fg: T.purple, border: '#4C1D9540' },
    orange: { bg: '#78350F20', fg: '#FDBA74', border: '#78350F40' },
  };
  const c = colors[variant] || colors.default;
  return (
    <span style={{
      padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, letterSpacing: 0.3,
      background: c.bg, color: c.fg, border: `1px solid ${c.border}`,
      display: 'inline-flex', alignItems: 'center',
    }}>{text}</span>
  );
}

function InfoRow({ label, value, mono }: { label: string; value: string | null | undefined; mono?: boolean }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #1E293B' }}>
      <span style={{ color: T.textMuted, fontSize: 11 }}>{label}</span>
      <span style={{ color: T.text, fontSize: 11, fontFamily: mono ? 'monospace' : 'inherit', maxWidth: '65%', textAlign: 'right' as const, wordBreak: 'break-all' as const }}>{value}</span>
    </div>
  );
}

function TagList({ label, raw, variant = 'default' as 'default' | 'success' | 'error' | 'info' | 'purple' | 'orange' }: { label: string; raw: string | null; variant?: 'default' | 'success' | 'error' | 'info' | 'purple' | 'orange' }) {
  if (!raw) return null;
  let items: string[] = [];
  try { items = JSON.parse(raw); } catch { items = raw.split(',').map(s => s.trim()).filter(Boolean); }
  if (!items.length) return null;
  return (
    <div style={{ marginTop: 8 }}>
      <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.8, marginBottom: 4 }}>{label}</div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
        {items.map((t: string) => <Chip key={t} text={t} variant={variant} />)}
      </div>
    </div>
  );
}

function MdBlock({ title, content }: { title: string; content: string | null }) {
  const [open, setOpen] = useState(false);
  if (!content) return null;
  return (
    <div style={{ marginTop: 6 }}>
      <Button variant="ghost" size="xs" onClick={() => setOpen(!open)} style={{ color: T.purple, padding: 0, fontSize: 11 }}>
        {open ? '▾' : '▸'} {title}
      </Button>
      {open && (
        <pre style={{
          background: T.bgSecondary, padding: 10, borderRadius: 6, fontSize: 10,
          color: T.textSecondary, maxHeight: 300, overflow: 'auto', whiteSpace: 'pre-wrap',
          border: `1px solid ${T.border}`, marginTop: 4, lineHeight: 1.5,
        }}>
          {content}
        </pre>
      )}
    </div>
  );
}

function ServerCard({ server, onUpdate, latestVersion }: { server: ServerRow; onUpdate: () => void; latestVersion: string }) {
  const [expanded, setExpanded] = useState(false);
  const [brain, setBrain] = useState<BrainSnapshot | null>(null);
  const [brainLoading, setBrainLoading] = useState(false);
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(server.display_name || server.hostname || '');
  const [saving, setSaving] = useState(false);
  const [updating, setUpdating] = useState(false);

  const isOutdated = server.runner_version && latestVersion && server.runner_version !== latestVersion;

  const triggerUpdate = async (e: Event) => {
    e.stopPropagation();
    if (updating) return;
    setUpdating(true);
    try {
      await api.post(`/cloud/admin/runners-v2/${server.id}/update`, {});
    } catch (err) { console.error(err); }
    setTimeout(() => setUpdating(false), 5000);
  };

  const saveName = async () => {
    if (!nameValue.trim()) return;
    setSaving(true);
    try {
      await api.put(`/cloud/admin/runners-v2/${server.id}/display-name`, { display_name: nameValue.trim() });
      setEditingName(false);
      onUpdate();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const effectiveStatus = server.runner_status || server.status;
  const agentName = server.mai_agent_name || server.agent_name;
  const agentModel = server.mai_model || server.runner_agent_model;
  const isOcInstalled = server.has_openclaw || server.openclaw_installed;

  const loadBrain = async () => {
    if (brain || brainLoading) return;
    setBrainLoading(true);
    try {
      const res = await api.get<{ brain: BrainSnapshot }>(`/cloud/admin/agents/${server.id}/brain`);
      setBrain(res.brain);
    } catch { /* ignore */ }
    setBrainLoading(false);
  };

  const handleExpand = () => {
    const next = !expanded;
    setExpanded(next);
    if (next) loadBrain();
  };

  const backupText = server.backup_enabled
    ? `Ativo${server.backup_schedule ? ` · ${server.backup_schedule}` : ''}${server.last_backup_at ? ` · ${timeAgo(server.last_backup_at)}` : ''}`
    : 'Sem backup';

  return (
    <div style={{
      background: T.bgSecondary, borderRadius: 8,
      border: `1px solid ${server.customer_email === 'adm@elontools.com' /* tenant-id */ ? '#FBBF2440' : expanded ? T.bgSecondary : T.border}`,
      overflow: 'hidden', transition: 'border-color 0.15s',
    }}>
      {/* Header */}
      <div
        onClick={handleExpand}
        style={{
          padding: '10px 16px', cursor: 'pointer', display: 'flex', alignItems: 'center',
          gap: 12, justifyContent: 'space-between',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0, flex: 1 }}>
          <OsIcon os={server.os} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              {editingName ? (
                <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                  <input
                    value={nameValue}
                    onInput={(e: any) => setNameValue(e.target.value)}
                    onKeyDown={(e: any) => { if (e.key === 'Enter') saveName(); if (e.key === 'Escape') setEditingName(false); }}
                    autoFocus
                    style={{ background: T.border, border: '1px solid #7C3AED', borderRadius: 4, color: T.text, padding: '2px 8px', fontSize: 13, fontWeight: 600, width: 200 }}
                  />
                  <Button variant="success" size="xs" onClick={saveName} disabled={saving}>
                    {saving ? '...' : 'Salvar'}
                  </Button>
                  <Button variant="secondary" size="xs" onClick={() => setEditingName(false)}>Cancelar</Button>
                </span>
              ) : (
                <span
                  style={{ color: T.text, fontWeight: 700, fontSize: 14, cursor: 'pointer', borderBottom: '1px dashed #334155' }}
                  title="Clique para editar nome"
                  onClick={(e) => { e.stopPropagation(); setEditingName(true); }}
                >
                  {server.display_name || server.hostname || server.id.slice(0, 12)}
                </span>
              )}
              <StatusDot status={effectiveStatus} />
              {server.runner_version && latestVersion && server.runner_version !== latestVersion && (
                <span style={{
                  padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 700,
                  background: 'T.warning20', color: T.warning, border: `1px solid ${T.warning40}`,
                  display: 'inline-flex', alignItems: 'center', gap: 4, animation: 'pulse 2s infinite',
                }}>
 v{server.runner_version} → {latestVersion}
                </span>
              )}
              <Chip text={(server.provider || 'N/A')} variant={server.provider === 'hostinger' ? 'purple' : 'info'} />
            </div>
            {/* Second line — compact info */}
            <div style={{ color: T.textMuted, fontSize: 11, marginTop: 3, display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
              {server.ipv4_address && <span>{server.ipv4_address}</span>}
              {agentName && <span style={{ color: T.purple }}>{agentName}</span>}
              {(() => {
                try { const a = server.agents_json ? JSON.parse(server.agents_json) : []; if (a.length > 1) return <span style={{ color: T.textSecondary }}>+{a.length - 1} agentes</span>; } catch {} return null;
              })()}
              {server.customer_email && (
                server.customer_email === 'adm@elontools.com' /* tenant-id */
                  ? <span style={{ color: T.warning, fontWeight: 700, background: '#FBBF2415', padding: '0 6px', borderRadius: 3, border: '1px solid #FBBF2430' }}>POOL · Disponível</span>
                  : <span>{server.customer_email}</span>
              )}
              <span>{timeAgo(server.last_seen_at || server.updated_at)}</span>
              <span style={{ color: T.textMuted }}>|</span>
              <span>Imagem: {server.last_snapshot_at ? timeAgo(server.last_snapshot_at) : 'Nunca'}</span>
              <span style={{ color: T.textMuted }}>|</span>
              <span style={{ color: server.backup_enabled ? '#6EE7B7' : '#F87171' }}>{backupText}</span>
            </div>
          </div>
        </div>
        {/* Right side badges */}
        <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexShrink: 0 }}>
          <Chip text={isOcInstalled ? 'OpenClaw' : 'Sem OC'} variant={isOcInstalled ? 'success' : 'error'} />
          {server.has_docker ? <Chip text="Docker" variant="info" /> : null}
          {isOutdated && (
            <Button
              variant="primary"
              size="xs"
              onClick={triggerUpdate}
              disabled={updating}
              loading={updating}
            >
              {updating ? 'Enviado...' : 'Atualizar'}
            </Button>
          )}
          <svg
            width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748B" strokeWidth="2"
            style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.15s' }}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </div>
      </div>

      {/* Expanded details */}
      {expanded && (
        <div style={{ padding: '0 16px 16px', borderTop: '1px solid #1E293B' }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginTop: 12 }}>
            {/* OpenClaw + Agent */}
            <div style={{ background: T.bgSecondary, borderRadius: 6, padding: 12, border: `1px solid ${T.border}` }}>
              <div style={{ color: T.textSecondary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.8, marginBottom: 8 }}>OpenClaw & Agente</div>
              <InfoRow label="Instalado" value={isOcInstalled ? 'Sim' : 'Não'} />
              <InfoRow label="Versão OC" value={server.openclaw_version} mono />
              <InfoRow label="Path" value={server.openclaw_path} mono />
              <InfoRow label="Agente" value={agentName} />
              <InfoRow label="Modelo" value={agentModel} />
              <InfoRow label="Último sync" value={timeAgo(server.synced_at || server.last_auto_sync)} />
              {/* Agents list */}
              {(() => {
                let agents: Array<{ name: string; model?: string; container?: string; status?: string }> = [];
                try { if (server.agents_json) agents = JSON.parse(server.agents_json); } catch {}
                if (!agents.length) return null;
                return (
                  <div style={{ marginTop: 8 }}>
                    <div style={{ color: T.textMuted, fontSize: 10, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.8, marginBottom: 4 }}>Agentes neste servidor ({agents.length})</div>
                    {agents.map((a, i) => (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: '1px solid #1E293B', fontSize: 11 }}>
                        <span style={{ color: T.purple, fontWeight: 600 }}>{a.name}</span>
                        <span style={{ color: T.textMuted }}>
                          {a.model && <span style={{ marginRight: 8 }}>{a.model}</span>}
                          {a.container && <span style={{ fontFamily: 'monospace', fontSize: 10 }}>{a.container}</span>}
                          {a.status && <Chip text={a.status} variant={a.status === 'running' ? 'success' : 'default'} />}
                        </span>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>

            {/* Runner */}
            <div style={{ background: T.bgSecondary, borderRadius: 6, padding: 12, border: `1px solid ${T.border}` }}>
              <div style={{ color: T.textSecondary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.8, marginBottom: 8 }}>Runner</div>
              <InfoRow label="ID" value={server.id?.slice(-12)} mono />
              <InfoRow label="Versão" value={server.runner_version} mono />
              <InfoRow label="OS" value={server.os} />
              <InfoRow label="Arch" value={server.arch} />
              <InfoRow label="Modo" value={server.mode} />
              <InfoRow label="Docker" value={server.has_docker ? (server.docker_version || 'Sim') : 'Não'} />
              <InfoRow label="Node" value={server.node_version} mono />
              <InfoRow label="Last seen" value={timeAgo(server.last_seen_at)} />
            </div>

            {/* Credentials */}
            <div style={{ background: T.bgSecondary, borderRadius: 6, padding: 12, border: `1px solid ${T.border}` }}>
              <div style={{ color: T.textSecondary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.8, marginBottom: 8 }}>Credenciais & Config</div>
              <InfoRow label="Cliente" value={server.customer_email} />
              <InfoRow label="env_path" value={server.env_path} mono />
              <InfoRow label="config_path" value={server.config_path} mono />
              <TagList label="Integrações" raw={server.integrations} variant="purple" />
              <TagList label="Env Keys" raw={server.env_keys} variant="orange" />
            </div>
          </div>

          {/* Timeline / Dates */}
          <div style={{ background: T.bgSecondary, borderRadius: 6, padding: 12, border: `1px solid ${T.border}`, marginTop: 12 }}>
            <div style={{ color: T.textSecondary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.8, marginBottom: 8 }}>Timeline</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 4 }}>
              <InfoRow label="Criado em" value={formatDate(server.created_at)} />
              <InfoRow label="Última atividade" value={formatDate(server.last_seen_at)} />
              <InfoRow label="Último sync agente" value={formatDate(server.synced_at)} />
              <InfoRow label="Último auto-sync" value={formatDate(server.last_auto_sync)} />
              <InfoRow label="Última imagem" value={formatDate(server.last_snapshot_at)} />
              <InfoRow label="Último backup" value={formatDate(server.last_backup_at || server.backup_config_last)} />
              <InfoRow label="Atualizado em" value={formatDate(server.updated_at)} />
            </div>
          </div>

          {/* Tools, Skills, Channels + Brain */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12, marginTop: 12 }}>
            <div style={{ background: T.bgSecondary, borderRadius: 6, padding: 12, border: `1px solid ${T.border}` }}>
              <TagList label="Tools" raw={server.tools} variant="info" />
              <TagList label="Skills" raw={server.skills} variant="success" />
              <TagList label="Channels" raw={server.channels} variant="orange" />
            </div>

            <div style={{ background: T.bgSecondary, borderRadius: 6, padding: 12, border: `1px solid ${T.border}` }}>
              <div style={{ color: T.textSecondary, fontSize: 11, fontWeight: 700, textTransform: 'uppercase' as const, letterSpacing: 0.8, marginBottom: 8 }}>Cérebro</div>
              {brainLoading && <span style={{ display: 'inline-block', verticalAlign: 'middle' }}><SkeletonBar width={80} height={12} /></span>}
              {brain ? (
                <>
                  <MdBlock title="SOUL.md" content={brain.soul_md} />
                  <MdBlock title="USER.md" content={brain.user_md} />
                  <MdBlock title="MEMORY.md" content={brain.memory_md} />
                  <MdBlock title="TOOLS.md" content={brain.tools_md} />
                  {brain.snapshot_at && <InfoRow label="Snapshot" value={timeAgo(brain.snapshot_at)} />}
                </>
              ) : null}
              {/* Workspace files from machine_agent_info */}
              <MdBlock title="AGENTS.md" content={server.agents_md} />
              <MdBlock title="HEARTBEAT.md" content={server.heartbeat_md} />
              <MdBlock title="IDENTITY.md" content={server.identity_md} />
              <MdBlock title="BOOTSTRAP.md" content={server.bootstrap_md} />
              {!brain && !brainLoading && !server.agents_md && !server.heartbeat_md && !server.identity_md && !server.bootstrap_md && (
                <span style={{ color: T.textMuted, fontSize: 11 }}>Sem dados</span>
              )}
            </div>
          </div>

          <MdBlock title="Config JSON" content={server.config_json} />
        </div>
      )}
    </div>
  );
}

export function AdminServers() {
  const [servers, setServers] = useState<ServerRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [providerFilter, setProviderFilter] = useState('all');

  useEffect(() => {
    loadServers();
  }, []);

  const loadServers = async () => {
    setLoading(true);
    try {
      const res = await api.get<{ servers: ServerRow[] }>('/cloud/admin/runners-v2');
      setServers(res.servers || []);
    } catch (e) {
      console.error('Failed to load servers:', e);
    }
    setLoading(false);
  };

  if (!isLoggedIn.value) return <LoginPage />;

  const filtered = servers.filter(s => {
    if (search) {
      const q = search.toLowerCase();
      const matchFields = [s.hostname, s.display_name, s.ipv4_address, s.customer_email, s.agent_name, s.id].filter(Boolean);
      if (!matchFields.some(f => f!.toLowerCase().includes(q))) return false;
    }
    if (statusFilter !== 'all') {
      const st = (s.runner_status || s.status || '').toLowerCase();
      if (statusFilter === 'online' && st !== 'online' && st !== 'running' && st !== 'active') return false;
      if (statusFilter === 'offline' && st !== 'offline') return false;
      if (statusFilter === 'failed' && st !== 'failed') return false;
    }
    if (providerFilter !== 'all') {
      if ((s.provider || '').toLowerCase() !== providerFilter) return false;
    }
    return true;
  });

  const providers = [...new Set(servers.map(s => (s.provider || 'unknown').toLowerCase()))].sort();

  // Detect latest runner version
  const latestVersion = servers
    .map(s => s.runner_version || '')
    .filter(Boolean)
    .sort((a, b) => {
      const pa = a.split('.').map(Number);
      const pb = b.split('.').map(Number);
      for (let i = 0; i < 3; i++) {
        if ((pa[i] || 0) !== (pb[i] || 0)) return (pb[i] || 0) - (pa[i] || 0);
      }
      return 0;
    })[0] || '';

  const onlineCount = servers.filter(s => {
    const st = (s.runner_status || s.status || '').toLowerCase();
    return st === 'online' || st === 'running' || st === 'active';
  }).length;
  const offlineCount = servers.filter(s => (s.runner_status || s.status || '').toLowerCase() === 'offline').length;
  const failedCount = servers.filter(s => (s.runner_status || s.status || '').toLowerCase() === 'failed').length;

  return (
    <div style={{ padding: '24px', maxWidth: 1200, margin: '0 auto' }}>
      <style>{`@keyframes pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.6; } }`}</style>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ color: T.text, fontSize: 20, fontWeight: 700, margin: 0, letterSpacing: -0.3 }}>
          Servidores
        </h1>
        <p style={{ color: T.textMuted, fontSize: 12, margin: '2px 0 0' }}>
          Cloud Machines · Runners · Agentes
        </p>
      </div>

      {/* Stats */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
        {[
          { label: 'Total', value: servers.length, color: T.textSecondary },
          { label: 'Online', value: onlineCount, color: T.success },
          { label: 'Offline', value: offlineCount, color: T.danger },
          { label: 'Falhou', value: failedCount, color: T.warning },
        ].map(s => (
          <div key={s.label} style={{
            background: T.bgSecondary, borderRadius: 6, padding: '6px 14px',
            border: `1px solid ${s.color}20`, minWidth: 60,
          }}>
            <div style={{ color: T.textMuted, fontSize: 10, textTransform: 'uppercase' as const, letterSpacing: 0.5 }}>{s.label}</div>
            <div style={{ color: s.color, fontSize: 18, fontWeight: 700 }}>{s.value}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Input
          placeholder="Buscar por nome, IP, email..."
          value={search}
          onChange={(v) => setSearch(v)}
          style={{
            background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 6,
            padding: '7px 12px', color: T.text, fontSize: 12, flex: 1, minWidth: 200, outline: 'none',
          }}
        />
        <Select
          value={statusFilter}
          onChange={(v) => setStatusFilter(v)}
          options={[{ value: 'all', label: 'Todos os status' }, { value: 'online', label: 'Online' }, { value: 'offline', label: 'Offline' }, { value: 'failed', label: 'Falhou' }]}
          style={{
            background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 6,
            padding: '7px 10px', color: T.textSecondary, fontSize: 12, outline: 'none',
          }}
        />
        <Select
          value={providerFilter}
          onChange={(v) => setProviderFilter(v)}
          options={[{ value: 'all', label: 'Todos os providers' }, ...providers.map(p => ({ value: p, label: p }))]}
          style={{
            background: T.bgSecondary, border: `1px solid ${T.border}`, borderRadius: 6,
            padding: '7px 10px', color: T.textSecondary, fontSize: 12, outline: 'none',
          }}
        />
        <Button variant="primary" size="sm" onClick={loadServers}>
          Atualizar
        </Button>
      </div>

      {/* Content */}
      {loading ? (
        <div style={{ padding: 12 }}>
          <SkeletonBar width="60%" height={12} />
          <div style={{ height: 6 }} />
          <SkeletonBar width="40%" height={12} />
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 40, color: T.textMuted, fontSize: 13 }}>
          Nenhum servidor encontrado
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {filtered.map(s => <ServerCard key={s.id} server={s} onUpdate={loadServers} latestVersion={latestVersion} />)}
        </div>
      )}
    </div>
  );
}
